/**
 * What a settings save changes, field by field (DEV-137).
 *
 * One definition, used twice: the back office shows it in the save bar's
 * review before a save, and the API records it in the History and decides
 * from it whether a reason is required. Keeping it here means the console
 * and the server can never disagree about what counts as "switching a rule
 * off".
 */

import { formatScore, formatWeight, type RiskModelDefinition } from '../risk-scoring/index.js';

/** One changed setting: a stable key, what people call it, and the value before and after. */
export interface SettingChange {
  readonly key: string;
  readonly label: string;
  /** Null when it did not exist before. */
  readonly before: string | null;
  /** Null when it no longer exists. */
  readonly after: string | null;
}

// ---- Transaction monitoring rules -----------------------------------------

/** The fields of a monitoring rule this needs; matches kyc.MonitoringRule. */
export interface MonitoringRuleLike {
  readonly id: string;
  readonly name: string;
  readonly enabled: boolean;
  readonly direction: string;
  readonly field: string;
  readonly amountEurMinor: string;
  readonly values: readonly string[];
}

const DIRECTION_LABELS: Readonly<Record<string, string>> = { ALL: 'All directions', IN: 'Incoming', OUT: 'Outgoing' };
const FIELD_LABELS: Readonly<Record<string, string>> = {
  AMOUNT_EUR: 'Amount in EUR',
  CUSTOMER_RISK: 'Customer risk rating',
  COUNTERPARTY_COUNTRY: 'Counterparty country',
};

function eur(minor: string): string {
  const value = minor.padStart(3, '0');
  return `€${value.slice(0, -2)}.${value.slice(-2)}`;
}

/** The condition of a rule, without its on/off state: "Outgoing · Amount in EUR exceeds €1000.00". */
function ruleCondition(rule: MonitoringRuleLike): string {
  const condition = rule.field === 'AMOUNT_EUR' ? `exceeds ${eur(rule.amountEurMinor)}` : `is one of ${rule.values.join(', ')}`;
  return `${rule.name} · ${DIRECTION_LABELS[rule.direction] ?? rule.direction} · ${FIELD_LABELS[rule.field] ?? rule.field} ${condition}`;
}

/** A rule in one line, on/off first. */
export function describeMonitoringRule(rule: MonitoringRuleLike): string {
  return `${rule.enabled ? 'On' : 'Off'} · ${ruleCondition(rule)}`;
}

/**
 * The rules a save switches off: enabled before, and disabled or removed
 * after. Removing an enabled rule stops it screening just as switching it off
 * does, so it counts. Names, in the order they were stored.
 */
export function switchedOffRules(
  before: readonly MonitoringRuleLike[],
  after: readonly MonitoringRuleLike[],
): string[] {
  const next = new Map(after.map((rule) => [rule.id, rule]));
  return before
    .filter((rule) => rule.enabled && next.get(rule.id)?.enabled !== true)
    .map((rule) => rule.name);
}

/**
 * Every rule change, in rule order (kept rules and new ones as listed after
 * the save, then removed ones). Switching a rule on or off is its own change,
 * so the History shows it on one line; other edits show the whole condition
 * before and after.
 */
export function monitoringRuleChanges(
  before: readonly MonitoringRuleLike[],
  after: readonly MonitoringRuleLike[],
): SettingChange[] {
  const previous = new Map(before.map((rule) => [rule.id, rule]));
  const kept = new Set(after.map((rule) => rule.id));
  const changes: SettingChange[] = [];
  for (const rule of after) {
    const old = previous.get(rule.id);
    if (old === undefined) {
      changes.push({ key: `rule:${rule.id}`, label: `Rule "${rule.name}" added`, before: null, after: describeMonitoringRule(rule) });
      continue;
    }
    if (old.enabled !== rule.enabled) {
      changes.push({ key: `rule:${rule.id}:enabled`, label: `Rule "${rule.name}" switched`,
        before: old.enabled ? 'On' : 'Off', after: rule.enabled ? 'On' : 'Off' });
    }
    if (ruleCondition(old) !== ruleCondition(rule)) {
      changes.push({ key: `rule:${rule.id}`, label: `Rule "${rule.name}"`, before: ruleCondition(old), after: ruleCondition(rule) });
    }
  }
  for (const rule of before) {
    if (!kept.has(rule.id)) {
      changes.push({ key: `rule:${rule.id}`, label: `Rule "${rule.name}" removed`, before: describeMonitoringRule(rule), after: null });
    }
  }
  return changes;
}

// ---- Risk scoring model ---------------------------------------------------

/**
 * What a new risk-scoring model version changes against the one before:
 * categories, criteria (name and weight), level scores and thresholds. The
 * first version is all additions.
 */
export function riskModelChanges(before: RiskModelDefinition | null, after: RiskModelDefinition): SettingChange[] {
  const changes: SettingChange[] = [];
  const compare = (key: string, label: string, was: string | undefined, now: string | undefined): void => {
    if (was !== now) changes.push({ key, label, before: was ?? null, after: now ?? null });
  };

  const oldCategories = new Map((before?.categories ?? []).map((c) => [c.id, c]));
  const newCategories = new Map(after.categories.map((c) => [c.id, c]));
  for (const id of new Set([...newCategories.keys(), ...oldCategories.keys()])) {
    const was = oldCategories.get(id);
    const now = newCategories.get(id);
    const name = now?.name ?? was?.name ?? id;
    compare(`category:${id}`, `Category "${name}"`, was?.name, now?.name);
    const oldCriteria = new Map((was?.criteria ?? []).map((x) => [x.id, x]));
    const newCriteria = new Map((now?.criteria ?? []).map((x) => [x.id, x]));
    for (const cid of new Set([...newCriteria.keys(), ...oldCriteria.keys()])) {
      const a = oldCriteria.get(cid);
      const b = newCriteria.get(cid);
      compare(
        `criterion:${cid}`,
        `Criterion "${name} › ${b?.name ?? a?.name ?? cid}"`,
        a === undefined ? undefined : `${a.name} — ${formatWeight(a.weightBp)}`,
        b === undefined ? undefined : `${b.name} — ${formatWeight(b.weightBp)}`,
      );
    }
  }

  const oldLevels = new Map((before?.levels ?? []).map((l) => [l.level, l]));
  const newLevels = new Map(after.levels.map((l) => [l.level, l]));
  for (const level of new Set([...newLevels.keys(), ...oldLevels.keys()])) {
    const a = oldLevels.get(level);
    const b = newLevels.get(level);
    compare(`level:${level}`, `Level ${level}`,
      a === undefined ? undefined : `${a.label} = ${String(a.score)}`,
      b === undefined ? undefined : `${b.label} = ${String(b.score)}`);
  }

  const oldThresholds = new Map((before?.thresholds ?? []).map((t) => [t.rating, t.minHundredths]));
  const newThresholds = new Map(after.thresholds.map((t) => [t.rating, t.minHundredths]));
  for (const rating of new Set([...newThresholds.keys(), ...oldThresholds.keys()])) {
    const a = oldThresholds.get(rating);
    const b = newThresholds.get(rating);
    compare(`threshold:${rating}`, `${rating} from`,
      a === undefined ? undefined : formatScore(a),
      b === undefined ? undefined : formatScore(b));
  }
  return changes;
}

// ---- Generic JSON (onboarding policy) -------------------------------------

/** JSON with object keys sorted, so a re-ordered object is not a change. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (typeof value === 'object' && value !== null) {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * Which top-level sections of a JSON document changed. A whole policy is too
 * long to show before and after, so a section that changed is named and its
 * value is summarised as "changed"; the version itself holds the full text.
 */
export function changedSections(
  before: Readonly<Record<string, unknown>> | null,
  after: Readonly<Record<string, unknown>>,
): SettingChange[] {
  const keys = [...new Set([...Object.keys(after), ...Object.keys(before ?? {})])].sort();
  return keys
    .filter((key) => canonicalJson(before?.[key]) !== canonicalJson(after[key]))
    .map((key) => {
      const was = before === null || !(key in before) ? undefined : before[key];
      const now = key in after ? after[key] : undefined;
      // A plain value (the version string) is shown as it is; a section as "changed".
      const scalar = (v: unknown): boolean => v === undefined || ['string', 'number', 'boolean'].includes(typeof v);
      if (scalar(was) && scalar(now)) {
        return { key: `section:${key}`, label: `Section "${key}"`,
          before: was === undefined ? null : String(was), after: now === undefined ? null : String(now) };
      }
      return { key: `section:${key}`, label: `Section "${key}"`, before: was === undefined ? null : 'as before',
        after: now === undefined ? null : 'changed' };
    });
}
